import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { 
  DeleteButton,
  ExportButton,
  FormButton,
  SuccessButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import * as actions from "../../_redux/homework-sources/HomeworkSourcesAction";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { useHomeworkSourcesUIContext } from "./HomeworkSourcesUIContext";
import { HomeworkSourcesFilter } from "./homework-sources-filter/HomeworkSourcesFilter";
import { HomeworkSourcesTable } from "./homework-sources-table/HomeworkSourcesTable";

export function HomeworkSourcesCard() {
  // Homework Sources UI Context
  const sourcesUIContext = useHomeworkSourcesUIContext();
  const sourcesUIProps = useMemo(() => {
    return {
      ids: sourcesUIContext.ids,
      setIds: sourcesUIContext.setIds,
      queryParams: sourcesUIContext.queryParams,
      openImportFromExcelDialog: sourcesUIContext.openImportFromExcelDialog,
      openNewHomeworkSourcePage: sourcesUIContext.openNewHomeworkSourcePage,
    };
  }, [sourcesUIContext]);
    
  // Getting curret state of Homework Source list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.homeworkSources }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Homework Sources Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Ödev Kaynak Listesini Excele Aktarır */
  const exportHomeworkSourcesToExcel = () => {
    dispatch(actions.getAllHomeworkSources(sourcesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [15, 40, 40, 20]
      const columns = ["Sınıf", "Ders", "Kaynak Adı", "Sayfa Numarası"];
      entities.forEach(entity => rows.push([entity.grade, entity.course, entity.name, entity.pageNumber]));
      const filename = "Ödev Kaynak Listesi";
      exportToExcel({ columns, rows, widths, filename });
    });    
  }

  /** Belli Bir Ödev Kaynak Listesini PDF e Aktarır */
  const exportHomeworkSourcesToPDF = () => {
    dispatch(actions.getAllHomeworkSources(sourcesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Sınıf",
          rows: entities.map(entity => entity.grade)
        },
        {
          header: "Ders",
          rows: entities.map(entity => entity.course)
        },
        {
          header: "Kaynak Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Sayfa Numarası",
          rows: entities.map(entity => entity.pageNumber)
        }
      ];
  
      const filename = "Ödev Kaynak Listesi";
      exportToPDF(views, filename);
    });    
  }  

  /** Belli Bir Ödev Kaynak Listesini Yazdırma Penceresini Açar */
  const printHomeworkSources = () => {
    dispatch(actions.getAllHomeworkSources(sourcesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = "Ödev Kaynak Listesi";
      const properties = ["Sınıf", "Ders", "Kaynak Adı", "Sayfa Numarası"];
  
      entities.forEach(entity => {
        data.push({
          "Sınıf": entity.grade,
          "Ders": entity.course,
          "Kaynak Adı": entity.name,
          "Sayfa Numarası": entity.pageNumber
        });
      });
  
      print({ data, properties, header });
    });    
  }

  /** Belli Bir Ödev Kaynak Listesini Panoya Kopyalar */
  const copyHomeworkSources = () => {
    dispatch(actions.getAllHomeworkSources(sourcesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const header = "Ödev Kaynak Listesi";
      const columns = ["Sınıf", "Ders", "Kaynak Adı", "Sayfa Numarası"];
      entities.forEach(entity => rows.push([entity.grade, entity.course, entity.name, entity.pageNumber]));
      copy({ header, columns, rows });
    });    
  }

  /** Belli Bir Ödev Kaynak Listesini Siler */
  const deleteHomeworkSources = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();        
      return;
    }

    deletionAlert().then(result => {
      if(result.isConfirmed) dispatch(actions.deleteHomeworkSources(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);

    if (isSuccess) {
      setTimeout(() => {
        sourcesUIProps.setIds([]);
        dispatch(actions.getHomeworkSources(sourcesUIProps.queryParams));
      }, 2500);      
    }
  };
  
  const title = <>Ödev Kaynak Listesi{sourcesUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{sourcesUIProps.ids.length}</span>]</> :
                  ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportHomeworkSourcesToExcel}
                exportToPDF={exportHomeworkSourcesToPDF}
                print={printHomeworkSources}
                copy={copyHomeworkSources}
              />
            }
            {
              isAdditionAuthorized &&
              <>
                <SuccessButton
                  path="Files/Import"
                  title="Excelden Yükle"
                  method={sourcesUIProps.openImportFromExcelDialog}
                />
                <FormButton 
                  path="Navigation/Plus"
                  title="Yeni Ödev Kaynağı"
                  method={sourcesUIProps.openNewHomeworkSourcePage}
                />
              </>
            }              
            {
              isDeletionAuthorized && 
              <DeleteButton 
                title="Seçilenleri Sil"
                method={() => deleteHomeworkSources(sourcesUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <HomeworkSourcesFilter />
        <HomeworkSourcesTable
          deleteHomeworkSources={deleteHomeworkSources}
        />
      </CardBody>
    </Card>
  );
}